/**
 * @typedef {import('../../shared/PropertyService/Valuation').default} Valuation
 * @typedef {import('../../shared/PropertyService/PropertyService').default} PropertyService
 * @typedef {import('./PropertyValuationsComponent').Props} Props
 * @typedef {import('./PropertyValuationsComponent').State} State
 */
import { withProperty } from '../PropertyProvider';
import { withPropertyService } from 'service-container';
import Loading from '../../../shared/Loading';
import React from 'react';
import PropertyValuationSummary from './PropertyValuationSummary';
import captureError from '../../../utils/captureError';
import { translate } from '../../Internationalization';
import NoContent from '../../../shared/NoContent';

/** @extends {React.Component<Props, State>} */
class PropertyValuations extends React.Component {
	/**
	 * @param {Props} props
	 */
	constructor(props) {
		super(props);
		/**
		 * @type {State}
		 */
		this.state = {
			isGettingData: true,
			isRequestingAccurateValuation: false,
			requestStatus: null,
			valuations: null,
		};
	}

	async componentDidMount() {
		try {
			const propertyId = this.props.property?.selectedProperty?.id;
			await this.#setValuations(propertyId);

			this.setState({
				isGettingData: false,
			});
		} catch (error) {
			captureError(error);

			this.setState({
				isGettingData: false,
			});
		}
	}

	/**
	 * @param {Props} prevProps
	 * @returns {Promise<void>}
	 */
	async componentDidUpdate(prevProps) {
		if (
			this.props.property?.selectedProperty?.id !==
			prevProps.property?.selectedProperty?.id
		) {
			await this.#setValuations(
				this.props.property?.selectedProperty?.id
			);
		}
	}

	/**
	 * @param {string | undefined} propertyId
	 * @returns {Promise<void>}
	 */
	async #setValuations(propertyId) {
		if (!propertyId) {
			this.setState({
				requestStatus: null,
				valuations: null,
			});
			return;
		}

		const valuations = await this.props.propertyService.getValuations(
			propertyId
		);

		this.setState({
			requestStatus: null,
			valuations,
		});
	}

	/**
	 * @returns {Promise<void>}
	 */
	async #requestAccurateValuation() {
		this.setState({
			isRequestingAccurateValuation: true,
			requestStatus: null,
		});

		try {
			await this.props.propertyService.requestAccurateValuation();

			this.setState({
				isRequestingAccurateValuation: false,
				requestStatus: 'success',
			});
		} catch (error) {
			captureError(error);

			this.setState({
				isRequestingAccurateValuation: false,
				requestStatus: 'error',
			});
		}
	}

	/**
	 * @returns {boolean}
	 */
	#hasValuations() {
		if (this.state.valuations === null) {
			return false;
		}

		if (this.state.valuations.size < 1) {
			return false;
		}

		for (const [, valuationList] of this.state.valuations) {
			if (valuationList.length > 0) {
				return true;
			}
		}

		return false;
	}

	/**
	 * @returns {JSX.Element |null}
	 */
	render() {
		if (this.state.isGettingData) {
			return <Loading />;
		}

		const { valuations } = this.state;

		const header = (
			<h2 className="h-ms-4 mb-6">
				{translate('homeowner.pages.finances.view.estimate.title')}
			</h2>
		);

		if (!this.#hasValuations()) {
			return (
				<section>
					{header}
					<NoContent />
				</section>
			);
		}

		if (valuations === null) {
			return (
				<section>
					{header}
					<NoContent />
				</section>
			);
		}

		return (
			<section className="mt-4">
				{header}
				<PropertyValuationSummary
					isRequestingAccurateValuation={
						this.state.isRequestingAccurateValuation
					}
					onRequestAccurateValuation={() =>
						this.#requestAccurateValuation()
					}
					requestStatus={this.state.requestStatus}
					valuations={valuations}
				/>
			</section>
		);
	}
}

export default withPropertyService(withProperty(PropertyValuations));
